<template>
  <div class="progress-graph-container">
    <div class="progress-graph" :class="createdClass">
      <span class="progress-graph-status-text">
        {{ getComponentUiText("app-progress-graph-created-1") }}
      </span>
    </div>

    <div class="progress-graph" :class="sentClass">
      <span class="progress-graph-status-text">
        {{ getComponentUiText("app-progress-graph-sent-1") }}
      </span>
    </div>

    <div class="progress-graph" :class="withVendorClass">
      <span class="progress-graph-status-text">
        {{ getComponentUiText("app-progress-graph-inprog-1") }}
      </span>
    </div>

    <div class="progress-graph" :class="readyClass">
      <span class="progress-graph-status-text">
        {{ getComponentUiText("app-progress-graph-review-1") }}
      </span>
    </div>

    <div class="progress-graph" :class="completedClass">
      <span class="progress-graph-status-text">
        {{ getComponentUiText("app-progress-graph-completed-1") }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IChecklistRequested } from "@/interfaces/vendor";
import { checklistRequestedStatusId } from "@/utils/constants";
import { getComponentUiText } from "@/utils/utilities";
import { onMounted, PropType, ref, watch } from "vue";

const createdClass = ref("");
const sentClass = ref("");
const withVendorClass = ref("");
const readyClass = ref("");
const completedClass = ref("");
// Created - new
// Sent - wait
// With vendor - inprog
// Ready to review - submit
// Completed - done
const props = defineProps({
  details: { type: Object as PropType<IChecklistRequested>, required: true },
});

onMounted(() => {
  checkStatus(props.details.statusId, props.details.isArchived);
});

watch(
  () => props.details,
  (newStatus) => {
    checkStatus(newStatus.statusId, newStatus.isArchived);
  },
  { deep: true },
);
const checkStatus = (status: string, isArchived: boolean) => {
  if (isArchived) {
    createdClass.value = "";
    sentClass.value = "";
    withVendorClass.value = "";
    readyClass.value = "";
    completedClass.value = "";
    return;
  }
  switch (status) {
    case checklistRequestedStatusId.created:
      createdClass.value = "status-progress";
      sentClass.value = "";
      withVendorClass.value = "";
      readyClass.value = "";
      completedClass.value = "";
      break;
    case checklistRequestedStatusId.waiting:
      createdClass.value = "status-progress active-divide";
      sentClass.value = "status-progress";
      withVendorClass.value = "";
      readyClass.value = "";
      completedClass.value = "";
      break;
    case checklistRequestedStatusId.inProgress:
      createdClass.value = "status-progress active-divide";
      sentClass.value = "status-progress active-divide";
      withVendorClass.value = "status-progress";
      readyClass.value = "";
      completedClass.value = "";
      break;
    case checklistRequestedStatusId.submitted:
      createdClass.value = "status-progress active-divide";
      sentClass.value = "status-progress active-divide";
      withVendorClass.value = "status-progress active-divide";
      readyClass.value = "status-progress";
      completedClass.value = "";
      break;
    case checklistRequestedStatusId.completed:
      createdClass.value = "status-progress active-divide";
      sentClass.value = "status-progress active-divide";
      withVendorClass.value = "status-progress active-divide";
      readyClass.value = "status-progress active-divide";
      completedClass.value = "status-completed";
      break;
    default:
      break;
  }
};
</script>

<style lang="scss" scoped>
.progress-graph-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.progress-graph {
  border-radius: 4px;
  display: flex;
  padding: 3px 5px;
  justify-content: center;
  align-items: center;
  min-width: 105px;
  border: 1px solid #eff0f1;
  background-color: #fcfcfd;
  position: relative;
  .progress-graph-status-text {
    font-size: 11px;
    font-weight: 500;
    line-height: 130%;
    color: #dbdbdb;
  }
  &::after {
    content: "";
    display: block;
    width: 80%;
    right: -94%;
    height: 1px;
    border-bottom: 1px dashed #dbdbdb;
    position: absolute;
  }
  &:last-child {
    &::after {
      display: none;
    }
  }
  &.status-progress {
    border-color: #e4f1ff;
    background-color: #f2f8ff;
    .progress-graph-status-text {
      color: #387dff;
    }
  }
  &.status-completed {
    border-color: #d9ede2;
    background-color: rgba(217, 237, 226, 0.6);
    .progress-graph-status-text {
      color: #01893d;
    }
  }
  &.active-divide {
    &::after {
      width: 105%;
      right: -105%;
      border-bottom: 1px solid #e4f1ff;
    }
  }
}
@media screen and (max-width: 1024px) {
  .progress-graph {
    &::after {
      content: "";
      width: 90%;
      right: -90%;
    }
  }
}
@media screen and (max-width: 768px) {
  .progress-graph {
    &::after {
      content: "";
      width: 105%;
      right: -105%;
    }
  }
}
@media screen and (max-width: 480px) {
  .progress-graph-container {
    flex-wrap: wrap;
  }
  .progress-graph {
    margin-bottom: 8px;
    &::after {
      content: "";
      width: 105%;
      right: -105%;
    }
    &.active-divide {
      &::after {
        width: 105%;
        right: -105%;
        border-bottom: 1px solid #e4f1ff;
      }
    }
  }
}
</style>
